<script>
	import { getContext, onDestroy, onMount } from 'svelte'
	import { admin } from '../../../library/scripts/PhpHandler.svelte'
	import { get_all_bookings } from '../AdminModule.svelte'
	import { isWithinInterval, intervalToDuration, isPast, isBefore, isEqual, addDays, format } from 'date-fns'
	import { get_username } from '../../../library/scripts/utils'
	import { getCookie } from '../../../library/scripts/cookie'
	import { slide } from 'svelte/transition'
	import { styles } from '../../../library/scripts/styles'
	import ThreeCols from '../../../library/columLayouts/ThreeCols.svelte'
	import Shader from '../../../library/ShadersAndSpinners/Shader.svelte'
	import Alert from '../../../modals/Alert.svelte'

	const globals = getContext('globals')
	const shadow = globals.boxShadow
	const fsX = globals.fsL
	const fsM = globals.fsN
	const colorMain = globals.colorMain
	const colorActive = globals.colorOk
	const colorPassive = globals.colorError

	//error handling
	let errorOffline = false
	let errorMessage = ''

	const userID = getCookie('id')

	const reset_error = () => {
		errorOffline = false
		errorMessage = ''
	}

	//load the bookings & booked periods from db
	let bookings = get_all_bookings()
	let bookedRooms = []
	let totalBookings = 0
	let totalDays = 0
	let closedBookings = 0
	let openBookings = 0
	let activeBookings = 0
	let closedTotal = 0
	let activeTotal = 0
	let openTotal = 0
	let totalRevenue = 0
	let currency = 'USD'

	//format the numbers
	let closedTotalF
	let openTotalF
	let activeTotalF
	let totalRevenueF

	//set dates
	let today = new Date()
	let tmrw = addDays(today, 1)
	today = format(today, 'dd/MM/yyyy')
	tmrw = format(tmrw, 'dd/MM/yyyy')
	let dateToday = today.split('/')
	const todayDate = new Date(dateToday[2], parseInt(dateToday[1]) - 1, dateToday[0])

	//all booked days
	let bookingDays

	//get the seasons from DB
	let seasonPeriods = []
	const seasons = admin({
		cmd: 'get_seasons',
	})

	seasons.then((s) => {
		s.fetch.forEach((period) => {
			let pStart = period[1].split('/')
			let pEnd = period[2].split('/')
			pStart = new Date(pStart[2], parseInt(pStart[1]) - 1, pStart[0])
			pEnd = new Date(pEnd[2], parseInt(pEnd[1]) - 1, pEnd[0])
			seasonPeriods.push({ start: pStart, end: pEnd })
		})
	})

	bookings.then((value) => {
		value.forEach((booking) => {
			currency = booking.currency
			bookedRooms.push({
				number: booking.room,
				checkIn: booking.checkin,
				checkOut: booking.checkout,
				high: booking.high,
				low: booking.low,
				total: booking.total,
			})
			//get total bookings
			totalBookings = value.length
		})

		//the total days (open,closed,total)
		bookedRooms.forEach((room) => {
			let bookStart = room.checkIn.split('/')
			let bookEnd = room.checkOut.split('/')
			bookStart = new Date(bookStart[2], parseInt(bookStart[1]) - 1, bookStart[0])
			bookEnd = new Date(bookEnd[2], parseInt(bookEnd[1]) - 1, bookEnd[0])

			//get the total days
			const duration = intervalToDuration({
				start: bookStart,
				end: bookEnd,
			})

			totalDays += duration.days

			//get open/closed
			let pastDates = isPast(bookEnd, todayDate)
			if (pastDates) {
				closedTotal += parseFloat(room.total)
				closedBookings += 1
			}

			let futureDates = isBefore(todayDate, bookStart)
			if (futureDates) {
				openTotal += parseFloat(room.total)
				openBookings += 1
			}

			let equalDates = isEqual(todayDate, bookStart)
			if (equalDates) {
				openTotal += parseFloat(room.total)
				openBookings += 1
			}

			//todays date is within interval
			let withinDate = isWithinInterval(todayDate, {
				start: bookStart,
				end: bookEnd,
			})

			if (withinDate && !isEqual(bookStart, todayDate) && !isEqual(bookEnd, todayDate)) {
				activeTotal += parseFloat(room.total)
				activeBookings += 1
			}
		})
		totalRevenue = activeTotal + openTotal + closedTotal

		closedTotalF = new Intl.NumberFormat('en-EN', { style: 'currency', currency: currency }).format(closedTotal)
		openTotalF = new Intl.NumberFormat('en-EN', { style: 'currency', currency: currency }).format(openTotal)
		activeTotalF = new Intl.NumberFormat('en-EN', { style: 'currency', currency: currency }).format(activeTotal)
		totalRevenueF = new Intl.NumberFormat('en-EN', { style: 'currency', currency: currency }).format(totalRevenue)
	})
</script>

{#await bookings}
	..loading
{:then r}
	<div transition:slide={{ duration: 500 }}>
		<div class="title" use:styles={{ fsX, colorMain }}>Summary of Bookings</div>
		<div class="booked_row">
			<div class="item_row item_active" use:styles={{ fsM, colorActive }}>
				<div>Active</div>
				<div>{activeBookings}</div>
			</div>

			<div class="item_row item_active" use:styles={{ fsM, colorActive }}>
				<div>Open</div>
				<div>{openBookings}</div>
			</div>

			<div class="item_row item_passive" use:styles={{ fsM, colorPassive }}>
				<div>Closed</div>
				<div>{closedBookings}</div>
			</div>

			<div class="item_row" use:styles={{ fsM, colorMain }}>
				<div>Total</div>
				<div>{totalBookings}</div>
			</div>

			<div class="item_row" use:styles={{ fsM, colorMain }}>
				<div>Days</div>
				<div>{totalDays}</div>
			</div>
		</div>
		<hr />
		<div class="title" use:styles={{ fsX, colorMain }}>Revenue</div>
		<ThreeCols column="260px" alignVertical="start">
			<div slot="column1">
				<div class="subtitle" use:styles={{ fsM, colorMain }}>Active Bookings</div>

				<div class="item_row" use:styles={{ fsM, colorMain }}>
					<div>Total</div>
					<div>{activeTotalF}</div>
				</div>
			</div>
			<div slot="column2">
				<div class="subtitle" use:styles={{ fsM, colorMain }}>Closed Bookings</div>

				<div class="item_row" use:styles={{ fsM, colorMain }}>
					<div>Total</div>
					<div>{closedTotalF}</div>
				</div>
			</div>
			<div slot="column3">
				<div class="subtitle" use:styles={{ fsM, colorMain }}>Open Bookings</div>
				<div class="item_row" use:styles={{ fsM, colorMain }}>
					<div>Total</div>
					<div>{openTotalF}</div>
				</div>

				<div class="item_row" use:styles={{ fsM, colorMain }}>
					<div />
					<div style="font-weight:bold; text-decoration:underline">{totalRevenueF}</div>
				</div>
			</div>
		</ThreeCols>
	</div>

	{#if errorOffline}
		<Shader>
			<div slot="content">
				<Alert message={errorMessage} showOkButton={false} alertMessage="ERROR" on:alertclose={reset_error} />
			</div>
		</Shader>
	{/if}
{/await}

<style>
	.booked_row {
		display: grid;
		grid-template-columns:
			clamp(30px, 13vw, 200px) clamp(30px, 13vw, 200px) clamp(30px, 13vw, 200px) clamp(30px, 13vw, 200px)
			clamp(30px, 13vw, 200px);
		column-gap: 10px;
		font-size: var(--fsS);
		color: var(--colorMain);
		margin-bottom: 15px;
	}

	.title {
		font-size: var(--fsX);
		font-weight: bold;
		color: var(--colorMain);
		margin: 30px 0 20px 20px;
	}

	.subtitle {
		font-size: var(--fsM);
		font-weight: bold;
		color: var(--colorMain);
		margin: 10px 0 20px 20px;
	}

	.item_row {
		width: 70%;
		display: grid;
		grid-template-columns: 80% 20%;
		color: var(--colorMain);
		font-size: var(--fsM);
		grid-row-gap: 15px;
		margin: 0px 0 20px 20px;
	}

	.item_active {
		color: var(--colorActive);
	}

	.item_passive {
		color: var(--colorPassive);
	}
</style>
